import { useEffect, useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { collection, query, where, orderBy, limit, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useMarket, filterByPortals } from '../../contexts/MarketContext';
import useBasePath from '../../utils/useBasePath';
import { useT } from '../../contexts/LocaleContext';
import {
  TOUR_CATEGORIES, CATEGORY_STYLES, CATEGORY_DOT,
  CATEGORY_COLORS, CATEGORY_FILLS,
} from '../../utils/statuses';
import { SkeletonTable, SkeletonList } from '../../components/Skeleton';
import EmptyState from '../../components/EmptyState';
import ConfirmDialog from '../../components/ConfirmDialog';
import SportIcon from '../../components/SportIcon';
import { totalTravellers } from '../../utils/groupSize';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Tour avatar — the club crest when one has been uploaded, otherwise the
 * category-tinted sport icon. Falls back to the icon if the crest fails to load.
 */
function TourAvatar({ req, fill, color }) {
  const [crestFailed, setCrestFailed] = useState(false);
  const showCrest = req.crestUrl && !crestFailed;

  return (
    <span
      className="w-[34px] h-[34px] rounded-[8px] flex items-center justify-center flex-shrink-0 overflow-hidden"
      style={showCrest
        ? { background: '#FFFFFF', border: '1px solid #E5E7EB' }
        : { background: fill, color }}
    >
      {showCrest ? (
        <img
          src={req.crestUrl}
          alt=""
          className="w-full h-full object-contain p-[2px]"
          onError={() => setCrestFailed(true)}
        />
      ) : (
        <SportIcon sport={req.sport} className="w-[18px] h-[18px]" />
      )}
    </span>
  );
}

function nightsCount(dep, ret) {
  if (!dep || !ret) return null;
  const diff = Math.round((new Date(ret) - new Date(dep)) / 864e5);
  return diff > 0 ? diff : null;
}

function daysUntil(str) {
  if (!str) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.round((new Date(str) - today) / 864e5);
}

// Nearest departure first: upcoming tours soonest → furthest, then tours that
// have already departed (most recent first), then any with no date yet.
function byNearestDeparture(a, b) {
  const dA = daysUntil(a.departureDate);
  const dB = daysUntil(b.departureDate);
  const rank = (d) => (d == null || Number.isNaN(d) ? 2 : d < 0 ? 1 : 0);
  const ra = rank(dA), rb = rank(dB);
  if (ra !== rb) return ra - rb;
  if (ra === 0) return dA - dB;
  if (ra === 1) return dB - dA;
  return 0;
}

const PAGE_SIZE = 50;
const ALL_FILTERS = ['all', ...TOUR_CATEGORIES, 'cancelled', 'archived'];

export default function Requests() {
  const navigate = useNavigate();
  const { isAdmin, currentUser } = useAuth();
  const { portals } = useMarket();
  const base = useBasePath();
  const t = useT();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);

  // Archived tours are hidden from every non-archived view and only loaded when
  // the "Archived" filter is selected — keeps them out of the default query window.
  const showArchived = filter === 'archived';

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, 'requests'),
      where('archived', '==', showArchived),
      orderBy('createdAt', 'desc'),
      limit(pageSize),
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        // CRM records live as real tour docs from the moment they're copied in
        // (so Quote/Letters/Messages work throughout the pipeline), but they
        // shouldn't clutter the Tours list until staff actually convert them.
        .filter((r) => !r.crmOrigin || r.crmFullyConverted);
      setTours(filterByPortals(docs, portals));
      setHasMore(snap.docs.length === pageSize);
      setLoading(false);
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, portals.join(','), showArchived]);

  async function handleDeleteTour() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await addDoc(collection(db, 'tourDeleteRequests'), {
        requestId: confirmDelete.id,
        requestedByUid: currentUser.uid,
        requestedAt: serverTimestamp(),
        status: 'pending',
      });
      setConfirmDelete(null);
    } catch (err) {
      console.error('Delete tour failed:', err);
      alert('Failed to delete tour. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  const inProgress = tours.filter((r) => ['tour_preparation','leader_handover','leader_signoff'].includes(r.category || 'sales_admin'));

  const counts = ALL_FILTERS.reduce((acc, f) => {
    if (f === 'archived') { acc[f] = showArchived ? tours.length : null; return acc; }
    acc[f] = f === 'all' ? tours.length : tours.filter((r) => (r.category || 'sales_admin') === f).length;
    return acc;
  }, {});

  const filtered = tours.filter((r) => {
    const cat = r.category || 'sales_admin';
    const matchesFilter = filter === 'all' || filter === 'archived' || cat === filter;
    const term = search.toLowerCase();
    const matchesSearch = !term || [r.sport, r.destination, r.customerEmail, r.contactName, r.groupType, r.tourName, r.internalRef]
      .some((v) => v?.toLowerCase().includes(term));
    return matchesFilter && matchesSearch;
  }).sort(byNearestDeparture);

  return (
    <div className="space-y-5">
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete tour?"
        message={confirmDelete ? `"${confirmDelete.label}" and all associated data will be permanently deleted. This cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete permanently'}
        danger
        loading={deleting}
        onConfirm={handleDeleteTour}
        onCancel={() => !deleting && setConfirmDelete(null)}
      />

      {/* ── Page header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">{t('tours.title')}</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">
            {tours.length} tour{tours.length !== 1 ? 's' : ''} {t('tours.countSuffix')} · {inProgress.length} {t('tours.inOperations')}
          </p>
        </div>
        <NavLink
          to={`${base}/requests/new`}
          className="flex items-center gap-1.5 bg-pb hover:bg-navy text-white text-[13px] font-bold px-[16px] py-[10px] rounded-[9px] transition shadow-sm whitespace-nowrap"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          {t('common.newTour')}
        </NavLink>
      </div>

      {/* ── Filter bar — mobile: dropdown; desktop: chip row ── */}
      <div className="md:hidden flex items-center gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-[9px] text-[13px] text-[#374151] bg-white focus:outline-none focus:ring-2 focus:ring-pb"
        >
          {ALL_FILTERS.map((f) => (
            <option key={f} value={f}>{t(`category.${f}`)}{counts[f] != null ? ` (${counts[f]})` : ''}</option>
          ))}
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
          className="border border-[#E5E7EB] rounded-[8px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb w-36"
        />
      </div>

      <div className="hidden md:flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-2 flex-1">
          {ALL_FILTERS.map((f) => {
            const active = filter === f;
            const dotColor = f !== 'all' && f !== 'cancelled' && f !== 'archived' ? CATEGORY_COLORS[f] : null;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex items-center gap-[6px] px-[11px] py-[7px] rounded-[9px] text-[12.5px] font-semibold transition border ${
                  active
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white border-[#E5E7EB] text-[#374151] hover:bg-surface'
                }`}
              >
                {dotColor && (
                  <span className="w-[6px] h-[6px] rounded-full flex-shrink-0"
                    style={{ background: active ? '#fff' : dotColor,
                      boxShadow: active ? `0 0 0 1.5px rgba(255,255,255,.5)` : undefined }} />
                )}
                {t(`category.${f}`)}
                {counts[f] != null && (
                  <span className={`text-[11px] font-bold px-[6px] py-[1px] rounded-[6px] tabular-nums ${
                    active ? 'bg-white/20 text-white' : 'bg-surface text-[#6B7280]'
                  }`}>
                    {counts[f]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 border border-[#E5E7EB] rounded-[9px] px-3 h-[38px] bg-white w-[230px]">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('tours.searchPlaceholder')}
            className="flex-1 bg-transparent text-[13px] text-[#374151] placeholder-[#9CA3AF] outline-none border-none"
          />
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <>
          <div className="hidden md:block"><SkeletonTable rows={6} cols={6} /></div>
          <div className="md:hidden"><SkeletonList count={4} /></div>
        </>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={search ? t('tours.noMatchTitle', { term: search }) : t('tours.noneTitle')}
          description={search ? t('tours.noMatchDesc') : t('tours.noneDesc')}
        />
      ) : (
        <>
          {/* ── Desktop table ── */}
          <div className="hidden md:block bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="bg-surface border-b border-[#E5E7EB]">
                    {['Tour','Dates','Group','Contact','Quote','Stage',''].map((h, i) => (
                      <th key={i} className={`px-[18px] py-[10px] text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.6px] ${i === 4 ? 'text-right' : 'text-left'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((req) => {
                    const cat = req.category || 'sales_admin';
                    const color = CATEGORY_COLORS[cat] || '#1A4A8A';
                    const fill = CATEGORY_FILLS[cat] || '#EBF2FC';
                    const nights = nightsCount(req.departureDate, req.returnDate);
                    const dl = daysUntil(req.departureDate);
                    return (
                      <tr
                        key={req.id}
                        className="border-b border-[#F1F3F6] hover:bg-surface transition-colors cursor-pointer group"
                        onClick={() => navigate(`${base}/requests/${req.id}`)}
                      >
                        {/* Tour */}
                        <td className="px-[18px] py-[12px]">
                          <div className="flex items-center gap-3">
                            <TourAvatar req={req} fill={fill} color={color} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="font-bold text-[13px] text-[#111827] truncate max-w-[210px]">
                                  {req.internalRef || req.tourName || req.sport}
                                </p>
                                {req.brand === 'your_tour' && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] bg-pb text-white flex-shrink-0">YT</span>
                                )}
                                {req.brand === 'padelante' && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] bg-[#8DC63F] text-[#1B4079] flex-shrink-0">PAD</span>
                                )}
                                {req.source === 'instant_quote' && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] bg-ba text-navy flex-shrink-0 whitespace-nowrap">
                                    Web Quote{req.submittedBy === 'staff' && req.salesAgentName ? ` · ${req.salesAgentName}` : ''}
                                  </span>
                                )}
                              </div>
                              <p className="text-[12px] text-[#6B7280] mt-0.5 truncate max-w-[210px]">
                                {req.internalRef ? (req.tourName || `${req.sport} — ${req.destination}`) : `${req.sport} — ${req.destination}`}
                              </p>
                            </div>
                          </div>
                        </td>
                        {/* Dates */}
                        <td className="px-[18px] py-[12px]">
                          <p className="text-[13px] text-[#374151] tabular-nums whitespace-nowrap">
                            {formatDate(req.departureDate)} → {formatDate(req.returnDate)}
                          </p>
                          <p className="text-[11.5px] text-[#9CA3AF] mt-0.5 tabular-nums">
                            {nights ? `${nights} nights` : '—'}
                            {dl !== null && ` · in ${dl}d`}
                          </p>
                        </td>
                        {/* Group */}
                        <td className="px-[18px] py-[12px]">
                          <p className="text-[13px] text-[#374151]">{req.groupType}</p>
                          <p className="text-[12px] text-[#9CA3AF] tabular-nums">{totalTravellers(req)} pax</p>
                        </td>
                        {/* Contact */}
                        <td className="px-[18px] py-[12px]">
                          <p className="text-[13px] text-[#374151] truncate max-w-[180px]">{req.contactName || '—'}</p>
                          <p className="text-[12px] text-[#9CA3AF] truncate max-w-[180px]">{req.customerEmail}</p>
                        </td>
                        {/* Quote */}
                        <td className="px-[18px] py-[12px] text-right">
                          {req.quoteDetails?.totalCostToCustomer != null ? (
                            <p className="text-[13px] font-bold text-[#111827] tabular-nums">
                              {req.currency === 'EUR' ? '€' : req.currency === 'USD' ? '$' : '£'}
                              {Number(req.quoteDetails.totalCostToCustomer).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          ) : (
                            <p className="text-[13px] text-[#9CA3AF]">—</p>
                          )}
                        </td>
                        {/* Stage */}
                        <td className="px-[18px] py-[12px]">
                          <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[20px] text-[11px] font-bold whitespace-nowrap ${CATEGORY_STYLES[cat] || 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                            <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${CATEGORY_DOT[cat] || 'bg-[#9CA3AF]'}`} />
                            {t(`category.${cat}`)}
                          </span>
                        </td>
                        {/* Action */}
                        <td className="px-[18px] py-[12px] text-right">
                          <div className="flex items-center justify-end gap-3">
                            {isAdmin && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmDelete({ id: req.id, label: req.internalRef || req.tourName || req.sport || req.destination || req.id }); }}
                                className="text-[12px] text-dr hover:text-[#7F1D1D] font-semibold transition"
                              >
                                {t('common.delete')}
                              </button>
                            )}
                            <span className="text-[12.5px] font-bold text-pb group-hover:text-navy transition">{t('common.view')}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Mobile cards ── */}
          <div className="md:hidden space-y-2">
            {filtered.map((req) => {
              const cat = req.category || 'sales_admin';
              return (
                <div
                  key={req.id}
                  onClick={() => navigate(`${base}/requests/${req.id}`)}
                  className="bg-white rounded-[12px] border border-[#E5E7EB] px-4 py-4 cursor-pointer active:bg-surface transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-[14px] text-[#111827]">{req.internalRef || req.tourName || req.sport}</p>
                        {req.brand === 'your_tour' && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] bg-pb text-white flex-shrink-0">YT</span>
                        )}
                        {req.brand === 'padelante' && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] bg-[#8DC63F] text-[#1B4079] flex-shrink-0">PAD</span>
                        )}
                        {req.source === 'instant_quote' && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] bg-ba text-navy flex-shrink-0 whitespace-nowrap">Web Quote</span>
                        )}
                      </div>
                      <p className="text-[13px] text-[#6B7280]">{req.internalRef ? (req.tourName || `${req.sport} — ${req.destination}`) : (req.tourName ? `${req.sport} — ${req.destination}` : req.destination)}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[20px] text-[11px] font-bold whitespace-nowrap flex-shrink-0 ${CATEGORY_STYLES[cat] || 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                      <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${CATEGORY_DOT[cat] || 'bg-[#9CA3AF]'}`} />
                      {t(`category.${cat}`)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-[#6B7280]">
                    <span className="tabular-nums">{formatDate(req.departureDate)} → {formatDate(req.returnDate)}</span>
                    {req.groupType && <span>· {req.groupType}</span>}
                    {totalTravellers(req) > 0 && <span className="tabular-nums">· {totalTravellers(req)} pax</span>}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div>
                      {req.contactName && <p className="text-[12px] text-[#374151]">{req.contactName}</p>}
                      {req.quoteDetails?.totalCostToCustomer != null && (
                        <p className="text-[13px] font-bold text-[#111827] tabular-nums">
                          {req.currency === 'EUR' ? '€' : req.currency === 'USD' ? '$' : '£'}
                          {Number(req.quoteDetails.totalCostToCustomer).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDelete({ id: req.id, label: req.internalRef || req.tourName || req.sport || req.destination || req.id }); }}
                          className="text-[12px] text-dr font-semibold py-1 px-2"
                        >
                          {t('common.delete')}
                        </button>
                      )}
                      <span className="text-[13px] text-pb font-bold">{t('common.view')}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {hasMore && (
        <div className="flex justify-center pt-2">
          <button
            onClick={() => setPageSize((p) => p + PAGE_SIZE)}
            className="px-[18px] py-[9px] text-[13px] font-bold text-pb border border-pb rounded-[8px] hover:bg-blue-light transition"
          >
            {t('tours.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}
